import React, { useState, useEffect } from 'react';
import { Solicitud } from '@/types/solicitud';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { MontoInput } from '@/components/ui/MontoInput';
import { useAuth } from '@/hooks/useAuth';
import { usePlazosDisponibles } from '@/hooks/usePlazosDisponibles';
import { empresaService } from '@/lib/api/empresaService';
import { User } from '@/types/user';
import { Empresa } from '@/types/empresa';
import { getAsesoresCached } from '@/hooks/useSharedData';

interface SolicitudFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<Solicitud>) => Promise<void>;
}

export const SolicitudFormModal: React.FC<SolicitudFormModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { user, isFieldAllowed } = useAuth();
  const canEditInheritedID = isFieldAllowed('inheritedID');
  const canEditClienteEmpresa = isFieldAllowed('clienteEmpresa');
  const canEditClienteNombre = isFieldAllowed('clienteNombre');
  const canEditClienteApellidoPaterno = isFieldAllowed('clienteApellidoPaterno');
  const canEditClienteApellidoMaterno = isFieldAllowed('clienteApellidoMaterno');
  const canEditClienteNuevoRenovacion = isFieldAllowed('clienteNuevoRenovacion');
  const canEditClientePuesto = isFieldAllowed('clientePuesto');
  const canEditClienteAntiguedad = isFieldAllowed('clienteAntiguedad');
  const canEditClienteClabe = isFieldAllowed('clienteClabe');
  const canEditClienteNombreJefe = isFieldAllowed('clienteNombreJefe');
  const canEditClienteCorreoJefe = isFieldAllowed('clienteCorreoJefe');
  const canEditClienteCorreo = isFieldAllowed('clienteCorreo');
  const canEditClienteSeEnteroPor = isFieldAllowed('clienteSeEnteroPor');
  const canEditProducto = isFieldAllowed('producto');
  const canEditMontoSolicitado = isFieldAllowed('montoSolicitado');
  const canEditPlazoSolicitado = isFieldAllowed('plazoSolicitado');
  const canEditPolizaSeguro = isFieldAllowed('polizaSeguro');
  const [formData, setFormData] = useState<Partial<Solicitud>>({
    inheritedID: '',
    producto: 'Nómina',
    clienteEmpresa: '',
    clienteNombre: '',
    clienteApellidoPaterno: '',
    clienteApellidoMaterno: '',
    clienteNuevoRenovacion: 'Nuevo',
    clientePuesto: '',
    clienteAntiguedad: '',
    clienteAntiguedadMeses: 0,
    clienteClabe: '',
    clienteNombreJefe: '',
    clienteCorreoJefe: '',
    clienteCorreo: '',
    clienteSeEnteroPor: '',
    polizaSeguro: '25000',
    montoSolicitado: undefined,
    plazoSolicitado: '',
    ownerID: user?.sub || '',
  });
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [asesores, setAsesores] = useState<User[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[] | null>(null);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const { plazos } = usePlazosDisponibles();

  // Actualizar ownerID cuando el usuario esté disponible
  useEffect(() => {
    if (user?.sub && !formData.ownerID) {
      setFormData(prev => ({ ...prev, ownerID: user.sub }));
    }
  }, [user?.sub]);

  // Si no tiene permiso para elegir asesor, asignar inheritedID al usuario actual al abrir
  useEffect(() => {
    if (isOpen && user?.sub && !canEditInheritedID && !formData.inheritedID) {
      setFormData(prev => ({ ...prev, inheritedID: user.sub }));
    }
  }, [isOpen, user?.sub, canEditInheritedID]);

  useEffect(() => {
    if (!isOpen) { setIsDirty(false); return; }
    let cancelled = false;

    // Usar caché compartido para asesores
    getAsesoresCached().then(data => {
      if (cancelled) return;
      setAsesores(data);
    });

    return () => { cancelled = true; };
  }, [isOpen]);

  // Cargar empresas cuando cambia el asesor (por selección manual o auto-asignación)
  useEffect(() => {
    if (!isOpen || !formData.inheritedID) {
      setEmpresas(null);
      setLoadingEmpresas(false);
      return;
    }
    let cancelled = false;
    setLoadingEmpresas(true);

    empresaService.getEmpresasPorAsesor(formData.inheritedID).then(resp => {
      if (cancelled) return;
      if (resp.data) {
        const sorted = [...resp.data].sort((a, b) =>
          (a.nombre || '').localeCompare(b.nombre || '', 'es')
        );
        setEmpresas(sorted);
      } else {
        setEmpresas([]);
      }
    }).catch(() => {
      if (!cancelled) setEmpresas([]);
    }).finally(() => {
      if (!cancelled) setLoadingEmpresas(false);
    });

    return () => { cancelled = true; };
  }, [formData.inheritedID, isOpen]);

  // Cargar empresas cuando se selecciona un asesor (Asesor Final)
  const handleAsesorChange = (ownerId: string) => {
    setFormData(prev => ({
      ...prev,
      inheritedID: ownerId,
      clienteEmpresa: '', // Limpiar empresa seleccionada
      voboRequerido: '', // Limpiar campos dependientes de empresa
      clientePagadora: '',
    }));
    // Las empresas se cargan automáticamente via el useEffect de formData.inheritedID
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Establecer fechaContacto automáticamente
      const now = new Date();
      const fechaContacto = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      
      await onSubmit({
        ...formData,
        fechaContacto,
        solicitudEstatus: 'CONTACTO',
        etapaEmbudo: 'CONTACTO',
        estatusEmbudo: 'CONTACTO',
        tipoDeNegocio: 'ACTIVO',
      });
      // Resetear formulario
      setIsDirty(false);
      setFormData({
        inheritedID: '',
        producto: 'Nómina',
        clienteEmpresa: '',
        clienteNombre: '',
        clienteApellidoPaterno: '',
        clienteApellidoMaterno: '',
        clienteNuevoRenovacion: 'Nuevo',
        clientePuesto: '',
        clienteAntiguedad: '',
        clienteAntiguedadMeses: 0,
        clienteClabe: '',
        clienteNombreJefe: '',
        clienteCorreoJefe: '',
        clienteCorreo: '',
        clienteSeEnteroPor: '',
        polizaSeguro: '25000',
        montoSolicitado: undefined,
        plazoSolicitado: '',
        ownerID: user?.sub || '',
      });
    } catch (error) {
      console.error('Error al crear solicitud:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof Solicitud, value: string | number | undefined) => {
    setIsDirty(true);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}  title="Nueva Solicitud de Crédito">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Asesor Final */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asesor Final <span className="text-red-500">*</span>
            </label>
            {canEditInheritedID ? (
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                value={formData.inheritedID || ''}
                onChange={e => handleAsesorChange(e.target.value)}
                required
              >
                <option value="">Seleccionar asesor...</option>
                {asesores.map(asesor => (
                  <option key={asesor.ownerID} value={asesor.ownerID}>
                    {asesor.name || asesor.userName}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                value={asesores.find(a => a.ownerID === formData.inheritedID)?.name || asesores.find(a => a.ownerID === formData.inheritedID)?.userName || formData.inheritedID || '—'}
                readOnly
              />
            )}
          </div>

          {/* Producto */}
          {canEditProducto && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Producto <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              value={formData.producto || 'Nómina'}
              onChange={e => {
                const producto = e.target.value;
                handleChange('producto', producto);
                // Adelanto de Nómina no tiene póliza de seguro
                if (producto === 'Adelanto de Nómina') {
                  handleChange('polizaSeguro', 'Ninguna');
                }
              }}
              required
            >
              <option value="Nómina">Nómina</option>
              <option value="Adelanto de Nómina">Adelanto de Nómina</option>
              <option value="Enganche Hipotecario">Enganche Hipotecario</option>
            </select>
          </div>
          )}

          {/* Empresa */}
          {canEditClienteEmpresa && (
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Empresa <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={formData.clienteEmpresa || ''}
              onChange={e => {
                const nombre = e.target.value;
                const emp = empresas?.find(em => em.nombre === nombre);
                setFormData(prev => ({
                  ...prev,
                  clienteEmpresa: nombre,
                  voboRequerido: emp?.voboRequerido || '',
                  clientePagadora: emp?.empresaPagadora || '',
                  // Limpiar antigüedad al cambiar de empresa (opciones diferentes para Llantera Atlas)
                  clienteAntiguedad: '',
                  clienteAntiguedadMeses: 0,
                }));
              }}
              required
              disabled={!formData.inheritedID || loadingEmpresas}
            >
              {!formData.inheritedID ? (
                <option value="">Primero selecciona un asesor...</option>
              ) : (loadingEmpresas || empresas === null) ? (
                <option value="">Cargando empresas...</option>
              ) : empresas.length === 0 ? (
                <option value="">No hay empresas asignadas a este asesor</option>
              ) : (
                <>
                  <option value="">Seleccionar empresa...</option>
                  {empresas.map(empresa => (
                    <option key={empresa.id} value={empresa.nombre}>
                      {empresa.nombre}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
          )}

          {/* Nombre */}
          {canEditClienteNombre && (
          <div>
            <Input
              label="Nombre"
              value={formData.clienteNombre || ''}
              onChange={e => handleChange('clienteNombre', e.target.value)}
              required
              placeholder="Nombre(s) del cliente"
            />
          </div>
          )}

          {/* Apellido Paterno */}
          {canEditClienteApellidoPaterno && (
          <div>
            <Input
              label="Apellido Paterno"
              value={formData.clienteApellidoPaterno || ''}
              onChange={e => handleChange('clienteApellidoPaterno', e.target.value)}
              required
              placeholder="Apellido paterno"
            />
          </div>
          )}

          {/* Apellido Materno */}
          {canEditClienteApellidoMaterno && (
          <div>
            <Input
              label="Apellido Materno"
              value={formData.clienteApellidoMaterno || ''}
              onChange={e => handleChange('clienteApellidoMaterno', e.target.value)}
              placeholder="Apellido materno"
            />
          </div>
          )}

          {/* Nuevo/Renovacion */}
          {canEditClienteNuevoRenovacion && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nuevo/Renovación <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              value={formData.clienteNuevoRenovacion || 'Nuevo'}
              onChange={e => handleChange('clienteNuevoRenovacion', e.target.value)}
              required
            >
              <option value="Nuevo">Nuevo</option>
              <option value="Renovación">Renovación</option>
            </select>
          </div>
          )}

          {/* Puesto */}
          {canEditClientePuesto && (
          <div>
            <Input
              label="Puesto"
              value={formData.clientePuesto || ''}
              onChange={e => handleChange('clientePuesto', e.target.value)}
              placeholder="Puesto del cliente"
              required
            />
          </div>
          )}

          {/* Antiguedad */}
          {canEditClienteAntiguedad && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Antigüedad <span className="text-red-500">*</span>
            </label>
            {(formData.clienteEmpresa || '').toUpperCase() === 'LLANTERA ATLAS' ? (
              // Selector especial para Llantera Atlas
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                value={formData.clienteAntiguedad || ''}
                onChange={e => {
                  handleChange('clienteAntiguedad', e.target.value);
                  // Actualizar meses según la selección para Llantera Atlas
                  let meses = 0;
                  if (e.target.value === '6 meses - 1 año') meses = 9;
                  else if (e.target.value === '1 año - 2 años') meses = 18;
                  else if (e.target.value === '2 años - 4 años') meses = 36;
                  else if (e.target.value === 'Más de 4 años') meses = 60;
                  else if (e.target.value === 'Tasa única excepción') meses = 60;
                  handleChange('clienteAntiguedadMeses', meses);
                }}
                required
              >
                <option value="">Seleccionar...</option>
                <option value="6 meses - 1 año">6 meses - 1 año</option>
                <option value="1 año - 2 años">1 año - 2 años</option>
                <option value="2 años - 4 años">2 años - 4 años</option>
                <option value="Más de 4 años">Más de 4 años</option>
                <option value="Tasa única excepción">Tasa única excepción</option>
              </select>
            ) : (
              // Selector normal para otras empresas
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
                value={formData.clienteAntiguedad || ''}
                onChange={e => {
                  handleChange('clienteAntiguedad', e.target.value);
                  // También actualizar meses según la selección
                  let meses = 0;
                  if (e.target.value === '0 - 6 meses') meses = 3;
                  else if (e.target.value === '6 meses - 1 año') meses = 9;
                  else if (e.target.value === '1 año - 2 años') meses = 18;
                  else if (e.target.value === 'Más de 2 años') meses = 30;
                  handleChange('clienteAntiguedadMeses', meses);
                }}
                required
              >
                <option value="">Seleccionar...</option>
                <option value="0 - 6 meses">0 - 6 meses</option>
                <option value="6 meses - 1 año">6 meses - 1 año</option>
                <option value="1 año - 2 años">1 año - 2 años</option>
                <option value="Más de 2 años">Más de 2 años</option>
              </select>
            )}
          </div>
          )}

          {/* Cuenta Clabe */}
          {canEditClienteClabe && (
          <div>
            <Input
              label="Cuenta CLABE"
              value={formData.clienteClabe || ''}
              onChange={e => handleChange('clienteClabe', e.target.value.replace(/\D/g, ''))}
              placeholder="Solo números"
              inputMode="numeric"
            />
          </div>
          )}

          {/* Nombre de Jefe de Inmediato */}
          {canEditClienteNombreJefe && (
          <div>
            <Input
              label="Nombre de Jefe de Inmediato"
              value={formData.clienteNombreJefe || ''}
              onChange={e => handleChange('clienteNombreJefe', e.target.value)}
              placeholder="Nombre completo del jefe"
              required
            />
          </div>
          )}

          {/* Correo de Jefe Inmediato */}
          {canEditClienteCorreoJefe && (
          <div>
            <Input
              label="Correo de Jefe Inmediato"
              type="email"
              value={formData.clienteCorreoJefe || ''}
              onChange={e => handleChange('clienteCorreoJefe', e.target.value)}
              placeholder="correo@ejemplo.com"
              required
            />
          </div>
          )}

          {/* Correo del Cliente */}
          {canEditClienteCorreo && (
          <div>
            <Input
              label="Correo del Cliente"
              type="email"
              value={formData.clienteCorreo || ''}
              onChange={e => handleChange('clienteCorreo', e.target.value)}
              placeholder="correo@ejemplo.com"
              required
            />
          </div>
          )}

          {/* Se enteró por */}
          {canEditClienteSeEnteroPor && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Se enteró por <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              value={formData.clienteSeEnteroPor || ''}
              onChange={e => handleChange('clienteSeEnteroPor', e.target.value)}
              required
            >
              <option value="">Seleccionar...</option>
              <option value="Correo del Asesor">Correo del Asesor</option>
              <option value="Correo Masivo">Correo Masivo</option>
              <option value="Flyer">Flyer</option>
              <option value="Llamada del Asesor">Llamada del Asesor</option>
              <option value="Presentacion del Asesor">Presentacion del Asesor</option>
              <option value="Recomendacion">Recomendacion</option>
            </select>
          </div>
          )}

          {/* Poliza de Seguro - No aplica para Adelanto de Nómina */}
          {canEditPolizaSeguro && formData.producto !== 'Adelanto de Nómina' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Póliza de Seguro <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              value={formData.polizaSeguro || '25000'}
              onChange={e => handleChange('polizaSeguro', e.target.value)}
              required
            >
              <option value="Ninguna">Ninguna</option>
              <option value="25000">$25,000</option>
              <option value="50000">$50,000</option>
              <option value="75000">$75,000</option>
              <option value="100000">$100,000</option>
            </select>
          </div>
          )}

          {/* Monto Solicitado */}
          {canEditMontoSolicitado && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto Solicitado ($) <span className="text-red-500">*</span>
            </label>
            <MontoInput
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm"
              value={formData.montoSolicitado}
              onChange={n => handleChange('montoSolicitado', n)}
              placeholder="Ingrese el monto"
              maxLength={16}
              required
            />
          </div>
          )}

          {/* Plazo Solicitado en meses */}
          {canEditPlazoSolicitado && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plazo Solicitado <span className="text-red-500">*</span></label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.plazoSolicitado || ''}
              onChange={e => handleChange('plazoSolicitado', e.target.value)}
              required
            >
              <option value="">Seleccionar...</option>
              {plazos.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Creando...' : 'Crear Solicitud'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
